import React, { useState, useEffect, useCallback } from 'react';
import { payoutAPI, overrideAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import MyCompensationTab from './MyCompensationTab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  DollarSign, FileText, CreditCard, Loader2, Download, Eye,
  ChevronLeft, ChevronRight, TrendingUp, Clock, CheckCircle2,
  Calendar, AlertCircle
} from 'lucide-react';

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '$0.00';
  return n < 0 ? `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

const STMT_STATUS = {
  Draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  Generated: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Approved: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300',
  Paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
};

const PAYMENT_TYPE_LABELS = {
  New: 'New Business',
  Fin_New: 'Financial New',
  Policy_Change: 'Policy Change',
  Adjustment: 'Adjustment',
  LOA: 'LOA',
  Other: 'Other'
};

export default function MyPayoutsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('compensation');
  const [loading, setLoading] = useState(true);

  // Override Statements
  const [statements, setStatements] = useState([]);
  const [stmtTotal, setStmtTotal] = useState(0);
  const [stmtPage, setStmtPage] = useState(1);
  const [stmtMonth, setStmtMonth] = useState('all');
  const [availableMonths, setAvailableMonths] = useState([]);

  // Statement Detail Dialog
  const [detailStmt, setDetailStmt] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalEarned: 0,
    totalPaid: 0,
    pendingAmount: 0,
    statementCount: 0
  });

  const pageSize = 20;

  // Load available months
  useEffect(() => {
    overrideAPI.months().then(res => {
      setAvailableMonths(res.data || []);
    }).catch(() => {});
  }, []);

  // Load override statements
  const loadStatements = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: stmtPage, page_size: pageSize };
      if (stmtMonth !== 'all') params.statement_month = stmtMonth;
      const res = await overrideAPI.statements.list(params);
      setStatements(res.data.items || []);
      setStmtTotal(res.data.total || 0);

      // Calculate summary stats from statements
      const items = res.data.items || [];
      const totalEarned = items.reduce((sum, s) => sum + (s.total_amount || 0), 0);
      const totalPaid = items.filter(s => s.status === 'Paid').reduce((sum, s) => sum + (s.total_amount || 0), 0);
      const pendingAmount = totalEarned - totalPaid;
      
      setSummaryStats({
        totalEarned,
        totalPaid,
        pendingAmount,
        statementCount: items.length
      });
    } catch (err) {
      console.error('Failed to load statements:', err);
      // Don't show error for 403 - user just doesn't have any statements
      if (err.response?.status !== 403) {
        toast.error('Failed to load statements');
      }
    }
    setLoading(false);
  }, [stmtPage, stmtMonth]);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  // Open statement detail
  const openStmtDetail = async (statementId) => {
    setDetailStmt(statementId);
    setDetailLoading(true);
    try {
      const res = await overrideAPI.statements.get(statementId);
      setDetailData(res.data);
    } catch (err) {
      console.error('Failed to load statement detail:', err);
      toast.error('Failed to load statement details');
    }
    setDetailLoading(false);
  };

  // Download PDF
  const handleDownloadPdf = async (statementId, payeeName, month) => {
    try {
      const res = await overrideAPI.statements.downloadPdf(statementId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_${payeeName.replace(/\s+/g, '_')}_${month}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download statement');
    }
  };

  // Pagination helpers
  const totalPages = Math.ceil(stmtTotal / pageSize);

  return (
    <div className="space-y-6" data-testid="my-payouts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Payouts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            View your override statements and payment history
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={stmtMonth} onValueChange={(v) => { setStmtMonth(v); setStmtPage(1); }}>
            <SelectTrigger className="w-40" data-testid="month-filter">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {availableMonths.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs: Compensation (Phase 3) + Override Statements */}
      <Tabs value={tab} onValueChange={setTab} data-testid="my-payouts-tabs">
        <TabsList>
          <TabsTrigger value="compensation" data-testid="tab-compensation">Compensation</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements">Override Statements</TabsTrigger>
        </TabsList>

        <TabsContent value="compensation" className="mt-6">
          <MyCompensationTab />
        </TabsContent>

        <TabsContent value="statements" className="mt-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Earned</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{fmtMoney(summaryStats.totalEarned)}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              From {summaryStats.statementCount} statements
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Paid</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{fmtMoney(summaryStats.totalPaid)}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Payments completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{fmtMoney(summaryStats.pendingAmount)}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Awaiting payment</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">Statements</CardTitle>
            <FileText className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{summaryStats.statementCount}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total statements</p>
          </CardContent>
        </Card>
      </div>

      {/* Statements List */}
      <Card>
        <CardHeader>
          <CardTitle>Statements</CardTitle>
          <CardDescription>Your payout statements with line-level detail</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : statements.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No Statements Yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your payout statements will appear here once generated.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statements.map(stmt => (
                    <TableRow key={stmt.statement_id} data-testid={`statement-row-${stmt.statement_id}`}>
                      <TableCell className="font-medium">{stmt.statement_month}</TableCell>
                      <TableCell className="font-semibold text-emerald-600">{fmtMoney(stmt.total_amount)}</TableCell>
                      <TableCell>{stmt.line_count}</TableCell>
                      <TableCell>
                        <Badge className={STMT_STATUS[stmt.status] || 'bg-gray-100 text-gray-700'}>
                          {stmt.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{stmt.payment_date || '-'}</TableCell>
                      <TableCell>{stmt.payment_method || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openStmtDetail(stmt.statement_id)}
                            data-testid={`view-stmt-${stmt.statement_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadPdf(stmt.statement_id, stmt.payee_name, stmt.statement_month)}
                            data-testid={`download-stmt-${stmt.statement_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {((stmtPage - 1) * pageSize) + 1} - {Math.min(stmtPage * pageSize, stmtTotal)} of {stmtTotal}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setStmtPage(p => Math.max(1, p - 1))}
                      disabled={stmtPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setStmtPage(p => Math.min(totalPages, p + 1))}
                      disabled={stmtPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Statement Detail Dialog */}
      <Dialog open={!!detailStmt} onOpenChange={(open) => { if (!open) { setDetailStmt(null); setDetailData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="statement-detail-dialog">
          <DialogHeader>
            <DialogTitle>Statement Details</DialogTitle>
            {detailData && (
              <DialogDescription>
                {detailData.statement_month} - {detailData.payee_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : detailData ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Amount</p>
                  <p className="text-lg font-bold text-emerald-600">{fmtMoney(detailData.total_amount)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                  <Badge className={STMT_STATUS[detailData.status] || 'bg-gray-100'}>{detailData.status}</Badge>
                </div>
                {detailData.payment_date && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Payment Date</p>
                    <p className="font-medium">{detailData.payment_date}</p>
                  </div>
                )}
                {detailData.reference_number && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Reference</p>
                    <p className="font-medium">{detailData.reference_number}</p>
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div>
                <h4 className="font-medium mb-3 text-gray-900 dark:text-white">Line Items ({detailData.line_count || detailData.line_items?.length || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Writing Agent</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailData.line_items || []).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.writing_agent_name || '-'}</TableCell>
                        <TableCell>{item.client_name || '-'}</TableCell>
                        <TableCell>{item.effective_date || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {PAYMENT_TYPE_LABELS[item.payment_type] || item.payment_type || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {fmtMoney(item.amount || item.allocation_amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Download Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={() => handleDownloadPdf(detailData.statement_id, detailData.payee_name, detailData.statement_month)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Failed to load statement details
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
